import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { useAtelierStore, JOB_STATUSES, getJobStatusFromOrdre } from "@store/atelierStore";
import { WorkshopJob, WorkshopJobStatus } from "../../types/garage/atelier";
import { AvailabilityBlock, ZoneType } from "@store/disponibiliteStore";
import { getMonday, toISO } from "@store/disponibiliteStore";
import NowLine from "./NowLine";
import { formatTime, formatDateHour, positionTooltip, offSegmentBg, PAUSE_SOLID } from "@utils/timeGrid";
import { OrdreNumHint, OrdrePlateHint } from "@components/ui/OrdreInterventionsHint";
import { showNotification } from "context/notification.service";
import {
  GridPreview,
  JobDragPreview,
  HOURS_START,
  HOURS_END,
  SNAP_HOUR,
  atelierMetrics,
  computeOverlapLayout,
} from "./gridTypes";
import { useDataStore } from "@store/user_org";
import { isOwnerOrAdmin } from "@utils/permissions";
import { useFullClosures, isDateClosed } from "@utils/closedDates";
import { absenceLabel, blockSegmentOnDate } from "@utils/blockSegments";
import OrdreModal from "@components/modals/OrdreModal";
import { navigateTo } from "context/navigation.service";
import { findOrdreForPlate, buildOrdreFromAppointment, getOrdreById, useOrdreStore } from "@store/ordreStore";
import OrdreStatusHint from "@components/ui/OrdreStatusHint";
import { jobMenuActions } from "./JobActionModals";
import type { Ordre, OrdreStatus } from "types/ordre";
import { PlanningContextMenu, ContextMenuState } from "@components/ui/PlanningContextMenu";

const CLICK_THRESHOLD = 5;

const DAY_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven"];

function getStatusConfig(status: WorkshopJobStatus) {
  return JOB_STATUSES.find((s) => s.id === status)!;
}

/** The ordre's status behind a job. Served with the job; the ordres store is only a
 *  fallback, since it holds what the « Ordres » screen happened to load. */
function jobOrdreStatus(job: WorkshopJob): OrdreStatus | undefined {
  return (job.ordreStatus as OrdreStatus | undefined)
    || (job.ordre_id ? getOrdreById(job.ordre_id)?.status : undefined);
}

/** Get the display status config for a job, derived from its linked ordre */
function getJobDisplayConfig(job: WorkshopJob) {
  const ordreStatus = jobOrdreStatus(job);
  if (ordreStatus) return getStatusConfig(getJobStatusFromOrdre(ordreStatus));
  return getStatusConfig(job.status);
}

/** Light background colors for dispo zone overlays on the atelier grid */
const ZONE_BG: Record<ZoneType, string> = {
  mecanique:      "#EEF0FF",
  carrosserie:    "#E4E7FF",
  peinture:       "#FEE2E2",
  diagnostique:   "#D1FAE5",
  service_rapide: "#ECE9FF",
  campagnes:      "#FFEDD5",
  administratif:  "#E2E8F0",
  pause:          PAUSE_SOLID, // même gris uni que les pauses entre deux plages
  conge:          "#E6E6E6",
};

/**
 * Return the zone type covering this hour for a worker, or null if none.
 * Honours multi-day/all-day spans. Congé wins over any work zone (a worker on
 * leave is never "available", even where a recurring work zone overlaps).
 */
function getDispoZone(
  blocks: AvailabilityBlock[],
  workerId: string,
  date: string,
  hour: number,
): ZoneType | null {
  let fallback: ZoneType | null = null;
  let congeHit = false;
  for (const b of blocks) {
    if (b.workerId !== workerId) continue;
    const seg = blockSegmentOnDate(b, date);
    if (!seg) continue;
    if (hour >= seg.startHour && hour < seg.endHour) {
      const zone = (b.zoneType as ZoneType) || "mecanique";
      if (zone === "conge") { congeHit = true; continue; }
      if (fallback === null) fallback = zone;
    }
  }
  return congeHit ? "conge" : fallback;
}

/** Subtract `holes` intervals from `base` intervals (both in hours). */
function subtractIntervals(base: [number, number][], holes: [number, number][]): [number, number][] {
  let segs = base;
  for (const [hs, he] of holes) {
    const next: [number, number][] = [];
    for (const [s, e] of segs) {
      if (he <= s || hs >= e) { next.push([s, e]); continue; }
      if (hs > s) next.push([s, hs]);
      if (he < e) next.push([he, e]);
    }
    segs = next;
  }
  return segs;
}

/** Merged intervals (in hours) where the worker actually works that day — congé subtracted. */
function workerWorkingIntervals(blocks: AvailabilityBlock[], workerId: string, date: string): [number, number][] {
  const work: [number, number][] = [];
  const conge: [number, number][] = [];
  for (const b of blocks) {
    if (b.workerId !== workerId) continue;
    const seg = blockSegmentOnDate(b, date);
    if (!seg) continue;
    if ((b.zoneType as ZoneType) === "conge") conge.push([seg.startHour, seg.endHour]);
    else work.push([seg.startHour, seg.endHour]);
  }
  work.sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];
  for (const [s, e] of work) {
    const last = merged[merged.length - 1];
    if (last && s <= last[1]) last[1] = Math.max(last[1], e);
    else merged.push([s, e]);
  }
  return subtractIntervals(merged, conge);
}

/** Off (non-working) sub-segments within the hour cell [h, h+1] — complement of the working intervals. */
function offSegmentsInHour(h: number, working: [number, number][]): [number, number][] {
  const segs: [number, number][] = [];
  let cursor = h;
  for (const [s, e] of working) {
    if (e <= h || s >= h + 1) continue;
    const ws = Math.max(s, h);
    if (ws > cursor) segs.push([cursor, ws]);
    cursor = Math.max(cursor, Math.min(e, h + 1));
  }
  if (cursor < h + 1) segs.push([cursor, h + 1]);
  return segs.filter(([s, e]) => e - s > 1e-9);
}

export function WeekTimeGridAtelier({
  blocks = [],
  workerId,
  gridScrollRef,
  gridPreview,
  setGridPreview,
  jobDragRef,
}: {
  blocks?: AvailabilityBlock[];
  workerId?: string;
  gridScrollRef?: React.RefObject<HTMLDivElement | null>;
  gridPreview?: GridPreview | null;
  setGridPreview?: (p: GridPreview | null) => void;
  jobDragRef?: React.MutableRefObject<{
    jobId: string;
    mode: "move" | "resize";
    startY: number;
    origStartHour: number;
    origDuration: number;
    origWorkerId: string;
  } | null>;
}) {
  const orgData = useDataStore((s) => s.orgData);
  const userData = useDataStore((s) => s.userData);

  // Subscribe to ordres so job colors update when ordre status changes
  const _ordres = useOrdreStore((s) => s.ordres);

  const selectedDate = useAtelierStore((s) => s.selectedDate);
  const compaction = useAtelierStore((s) => s.compaction);
  // Créneaux dont l'écriture MyO est en file ou en cours : affichés « en cours ».
  const myoPendingJobIds = useAtelierStore((s) => s.myoPendingJobIds);
  // Dynamic row-height metrics (shadow the module defaults) for the compaction setting.
  const { HOUR_BLOCK_HEIGHT, SNAP_PX } = atelierMetrics(compaction);
  // Quarter-hour cell height derived from the hour height (was hardcoded 32,
  // which broke every density except the 128px "normal").
  const QUARTER_H = HOUR_BLOCK_HEIGHT / 4;
  const activeSpecialties = useAtelierStore((s) => s.activeSpecialties);
  const allWorkers = useAtelierStore((s) => s.workers);
  const allJobs = useAtelierStore((s) => s.jobs);
  const searchQuery = useAtelierStore((s) => s.searchQuery);
  const hoveredChargeId = useAtelierStore((s) => s.hoveredChargeId);
  const charges = useAtelierStore((s) => s.charges);
  const storeCarWindows = useAtelierStore((s) => s.carWindows);
  const confirmJob = useAtelierStore((s) => s.confirmJob);

  // Use persistent carWindows from store for conflict detection (survives charge deletion)
  const carWindowByOrdre = storeCarWindows;

  const isJobOutsideCarWindow = useCallback((job: WorkshopJob): boolean => {
    if (!job.ordre_id) return false;
    const cw = carWindowByOrdre[job.ordre_id];
    if (!cw || !cw.depositDate) return false;
    const restDate = cw.restitutionDate || cw.depositDate;
    if (job.date < cw.depositDate || job.date > restDate) return true;
    const jobEnd = job.startHour + job.durationHours;
    if (job.date === cw.depositDate && cw.depositStartHour != null && job.startHour < cw.depositStartHour) return true;
    if (job.date === restDate && cw.restitutionStartHour != null && jobEnd > cw.restitutionStartHour) return true;
    return false;
  }, [carWindowByOrdre]);

  const highlightOrdreId = useAtelierStore((s) => s.highlightOrdreId);
  const highlightJobId = useAtelierStore((s) => s.highlightJobId);

  useEffect(() => {
    if (!highlightJobId || !gridScrollRef?.current) return;
    const job = allJobs.find((j: WorkshopJob) => j.id === highlightJobId);
    if (!job) return;
    const scrollParent = gridScrollRef.current.closest(".overflow-auto") as HTMLElement | null;
    if (scrollParent) {
      const targetPx = (job.startHour - HOURS_START) * HOUR_BLOCK_HEIGHT;
      scrollParent.scrollTo({ top: targetPx - 80, behavior: "smooth" });
    }
  }, [highlightJobId, allJobs]);

  const [selectedOrdre, setSelectedOrdre] = useState<Ordre | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [hoveredJobId, setHoveredJobId] = useState<string | null>(null);
  const clickStartRef = useRef<{ x: number; y: number } | null>(null);

  const pendingDragRef = useRef<{
    job: WorkshopJob;
    mode: "move" | "resize";
    startX: number;
    startY: number;
  } | null>(null);

  // Cache dragged job so preview survives when allJobs is replaced by a week-change fetch
  const draggedJobRef = useRef<WorkshopJob | null>(null);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent, job: WorkshopJob, mode: "move" | "resize") => {
      if (e.button === 2) return;
      if (!jobDragRef || !setGridPreview) return;
      e.preventDefault();
      e.stopPropagation();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      clickStartRef.current = { x: e.clientX, y: e.clientY };
      pendingDragRef.current = {
        job,
        mode,
        startX: e.clientX,
        startY: e.clientY,
      };
    },
    [jobDragRef, setGridPreview],
  );

  const handlePointerMoveLocal = useCallback(
    (e: React.PointerEvent) => {
      const pending = pendingDragRef.current;
      if (!pending || !jobDragRef || !setGridPreview || jobDragRef.current) return;
      const dx = Math.abs(e.clientX - pending.startX);
      const dy = Math.abs(e.clientY - pending.startY);
      if (dx >= CLICK_THRESHOLD || dy >= CLICK_THRESHOLD) {
        // Release pointer capture so global handler takes over (survives re-renders)
        try { (e.target as HTMLElement).releasePointerCapture(e.pointerId); } catch (_) {}
        const { job, mode } = pending;

        // Teleport to green window if job is outside it
        if (mode === "move") {
          const cw = job.ordre_id ? carWindowByOrdre[job.ordre_id] : null;
          if (!cw || !cw.depositDate) {
            const reason = !job.ordre_id
              ? "Aucun ordre associé à ce travail"
              : "Aucune fenêtre véhicule définie (pas de date de réception)";
            showNotification({ type: "default", message: `Pas de fenêtre véhicule : ${reason}` });
          } else if (isJobOutsideCarWindow(job)) {
            const { selectedDate, setSelectedDate } = useAtelierStore.getState();
            const currentMonday = getMonday(selectedDate);
            const currentFriday = new Date(currentMonday);
            currentFriday.setDate(currentMonday.getDate() + 4);
            const mondayISO = toISO(currentMonday);
            const fridayISO = toISO(currentFriday);
            const restDate = cw.restitutionDate || cw.depositDate;
            // Only teleport if current week has NO overlap with the reception–restitution window
            const weekOverlapsWindow = cw.depositDate <= fridayISO && restDate >= mondayISO;
            if (!weekOverlapsWindow) {
              setSelectedDate(cw.depositDate);
            }
          }
        }

        draggedJobRef.current = job;
        jobDragRef.current = {
          jobId: job.id,
          mode,
          startY: pending.startY,
          origStartHour: job.startHour,
          origDuration: job.durationHours,
          origWorkerId: job.workerId,
        };
        setGridPreview({
          type: "job",
          jobId: job.id,
          startHour: job.startHour,
          durationHours: job.durationHours,
          workerId: job.workerId,
          date: job.date,
        });
        pendingDragRef.current = null;
      }
    },
    [setGridPreview, jobDragRef, carWindowByOrdre, isJobOutsideCarWindow],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent, job: WorkshopJob) => {
      pendingDragRef.current = null;
      const start = clickStartRef.current;
      if (
        start &&
        Math.abs(e.clientX - start.x) < CLICK_THRESHOLD &&
        Math.abs(e.clientY - start.y) < CLICK_THRESHOLD
      ) {
        if (job.autoGenerated) {
          confirmJob(job.id);
          clickStartRef.current = null;
          return;
        }
        if (job.ordre_id) {
          // La fiche d'ordre, plus la modale « Modifier RDV » : plus complète, plus
          // d'actions, horaires compris (Héric 2026-10-13).
          navigateTo({ route: `/ordre/${job.ordre_id}` });
        } else {
          setSelectedOrdre(findOrdreForPlate(job.plate) || buildOrdreFromAppointment(job));
        }
      }
      clickStartRef.current = null;
    },
    [confirmJob],
  );

  const isTousMode = workerId === "tous";
  const worker = isTousMode ? null : (allWorkers.find((w) => w.id === workerId) || allWorkers[0]);

  const closedDates = useFullClosures();
  const monday = useMemo(() => getMonday(selectedDate), [selectedDate]);
  const weekDates = useMemo(
    () => Array.from({ length: 5 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return toISO(d);
    }),
    [monday],
  );

  const hours = Array.from(
    { length: HOURS_END - HOURS_START },
    (_, i) => HOURS_START + i,
  );

  // Non-working hours per day (from dispo atelier blocks) — greyed out when a
  // single worker is shown. Empty in "tous" mode.
  const offIntervalsByDate = useMemo(() => {
    const map: Record<string, [number, number][]> = {};
    if (isTousMode || !worker) return map;
    for (const date of weekDates) map[date] = workerWorkingIntervals(blocks, worker.id, date);
    return map;
  }, [isTousMode, worker, blocks, weekDates]);

  // Jobs for this worker (or all workers in "tous" mode) this week
  const jobsByDay = useMemo(() => {
    if (!isTousMode && !worker) return {};
    const map: Record<string, WorkshopJob[]> = {};
    for (const date of weekDates) map[date] = [];
    for (const j of allJobs) {
      if (!isTousMode && j.workerId !== worker!.id) continue;
      if (!weekDates.includes(j.date)) continue;
      // Un compagnon seul : tous ses jobs, quel que soit leur métier (ils occupent son temps).
      if (isTousMode && activeSpecialties.length > 0 && !activeSpecialties.includes(j.specialty)) continue;
      if (
        searchQuery &&
        !j.clientName.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !j.plate.toLowerCase().includes(searchQuery.toLowerCase())
      )
        continue;
      if (map[j.date]) map[j.date].push(j);
    }
    return map;
  }, [worker, isTousMode, allJobs, weekDates, activeSpecialties, searchQuery]);

  // Overlap layout per day : en mode « tous » comme pour un seul compagnon, deux
  // jobs au même créneau se partagent la largeur (voir WorkerTimeGrid).
  const overlapByDay = useMemo(() => {
    const result: Record<string, Map<string, { column: number; totalColumns: number }>> = {};
    for (const date of weekDates) {
      const dayJobs = jobsByDay[date] || [];
      if (dayJobs.length > 0) {
        result[date] = computeOverlapLayout(dayJobs.map((j) => ({
          id: j.id,
          startHour: j.startHour,
          durationHours: j.durationHours,
        })));
      }
    }
    return result;
  }, [weekDates, jobsByDay]);

  // Worker name lookup for "tous" mode labels
  const workerNameById = useMemo(() => {
    if (!isTousMode) return {};
    const map: Record<string, string> = {};
    for (const w of allWorkers) map[w.id] = w.name;
    return map;
  }, [isTousMode, allWorkers]);

  // Resolve carWindow: from hovered charge, hovered job, or the item being dragged
  const activeCarWindow = useMemo((): import("../../types/garage/atelier").CarWindow | null => {
    if (hoveredChargeId) {
      const charge = charges.find((c) => c.id === hoveredChargeId);
      if (charge?.carWindow) return charge.carWindow;
      if (charge?.ordre_id) return storeCarWindows[charge.ordre_id] || null;
      return null;
    }
    // Hovered job on the grid (not dragging)
    if (hoveredJobId && !gridPreview) {
      const job = allJobs.find((j) => j.id === hoveredJobId);
      if (job?.carWindow) return job.carWindow;
      if (job?.ordre_id) {
        return charges.find((c) => c.ordre_id === job.ordre_id && c.carWindow)?.carWindow
          || storeCarWindows[job.ordre_id] || null;
      }
    }
    if (gridPreview?.type === "job") {
      const job = allJobs.find((j) => j.id === gridPreview.jobId);
      if (job?.carWindow) return job.carWindow;
      if (job?.ordre_id) {
        return charges.find((c) => c.ordre_id === job.ordre_id && c.carWindow)?.carWindow
          || storeCarWindows[job.ordre_id] || null;
      }
    }
    if (gridPreview?.type === "charge") {
      const charge = charges.find((c) => c.id === (gridPreview as any).chargeId);
      if (charge?.carWindow) return charge.carWindow;
      if (charge?.ordre_id) return storeCarWindows[charge.ordre_id] || null;
    }
    return null;
  }, [hoveredChargeId, hoveredJobId, charges, gridPreview, allJobs, storeCarWindows]);

  // Compute car availability overlay per day
  const carOverlayByDay = useMemo(() => {
    if (!activeCarWindow) return {};
    const cw = activeCarWindow;
    if (!cw.depositDate) return {};
    const restDate = cw.restitutionDate || cw.depositDate;
    const result: Record<string, { startHour: number; endHour: number }> = {};
    for (const date of weekDates) {
      if (date < cw.depositDate || date > restDate) continue;
      const startHour = date === cw.depositDate && cw.depositStartHour != null
        ? cw.depositStartHour : HOURS_START;
      const endHour = date === restDate && cw.restitutionStartHour != null
        ? cw.restitutionStartHour : HOURS_END;
      result[date] = { startHour, endHour };
    }
    return result;
  }, [activeCarWindow, weekDates]);

  const headerHeight = 28;
  const hourLabelWidth = 32;
  const minuteColWidth = 24;

  if (!isTousMode && !worker) {
    return (
      <div className="flex items-center justify-center p-10 text-neutral-400" style={{ fontFamily: "Poppins" }}>
        Aucun mécanicien trouvé
      </div>
    );
  }

  return (
    <div
      className="relative overflow-auto"
      style={{
        border: "1px solid #D9D9D9",
        borderRadius: 10,
        // Comme le planning des locs : la grille défile seule, l'en-tête reste collé
        // en haut. La classe overflow-auto sert aussi au « scroll vers le job ».
        overflow: "auto",
        flex: "1 1 auto",
        minHeight: 320,
      }}
    >
      {/* Column headers — days of the week */}
      <div className="flex flex-row" style={{ height: headerHeight, position: "sticky", top: 0, zIndex: 50, background: "#FFF" }}>
        <div
          style={{
            width: hourLabelWidth + minuteColWidth + 1,
            borderRight: "1px solid #D9D9D9",
            borderBottom: "1px solid #D9D9D9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "Poppins",
            fontSize: 11,
            color: "#A1A1A1",
            fontWeight: 500,
          }}
        >
          {isTousMode ? "Tous" : worker!.name}
        </div>
        {weekDates.map((date, i) => {
          const day = new Date(date + "T00:00:00").getDate();
          const isToday = date === toISO(new Date());
          const isClosed = isDateClosed(date, closedDates);
          const absence = !isTousMode && worker ? absenceLabel(blocks, worker.id, date) : null;
          return (
            <div
              key={date}
              className="flex-1 flex items-center justify-center gap-1 text-sm font-medium whitespace-nowrap"
              style={{
                borderRight: "1px solid #D9D9D9",
                borderBottom: "1px solid #D9D9D9",
                background: isClosed ? "#FEE2E2" : isToday ? "#EEF2FF" : "#FFF",
                color: isClosed ? "#DC2626" : isToday ? "#6366F1" : "#525252",
                fontFamily: "Poppins",
              }}
            >
              {DAY_LABELS[i]} {day}
              {isClosed && (
                <span className="text-h6 font-semibold px-1 rounded" style={{ background: "#FECACA", color: "#DC2626" }}>
                  Fermé
                </span>
              )}
              {absence && (
                <span title={absence} className="truncate rounded px-1.5 text-[11px] leading-[18px]" style={{ background: "#EFD9FF", color: "#9333EA", maxWidth: "60%" }}>
                  {absence}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Grid body */}
      <div className="relative" ref={gridScrollRef}>
        {weekDates.map((date, i) => {
          const hourLabelW = hourLabelWidth + minuteColWidth + 1;
          return (
            <NowLine
              key={`now-${date}`}
              date={date}
              hourBlockHeight={HOUR_BLOCK_HEIGHT}
              left={`calc(${hourLabelW}px + (100% - ${hourLabelW}px) * ${i / weekDates.length})`}
              width={`calc((100% - ${hourLabelW}px) / ${weekDates.length})`}
            />
          );
        })}
        {/* Car availability overlays per day */}
        {activeCarWindow && Object.keys(carOverlayByDay).length > 0 && (() => {
          const totalHeight = (HOURS_END - HOURS_START) * HOUR_BLOCK_HEIGHT;
          const hourLabelW = hourLabelWidth + minuteColWidth + 1;
          // Each day column is flex-1, so we calculate proportionally
          const dayCount = weekDates.length;
          return weekDates.map((date, i) => {
            const overlay = carOverlayByDay[date];
            const colLeft = `calc(${hourLabelW}px + ${(i / dayCount) * 100}% * (1 - ${hourLabelW}px / 100%))`;
            const colWidth = `calc((100% - ${hourLabelW}px) / ${dayCount})`;
            if (!overlay) {
              // Day is outside car window — dim entire column
              return (
                <div
                  key={`dim-${date}`}
                  className="absolute pointer-events-none"
                  style={{
                    top: 0,
                    left: `calc(${hourLabelW}px + (100% - ${hourLabelW}px) * ${i / dayCount})`,
                    width: `calc((100% - ${hourLabelW}px) / ${dayCount})`,
                    height: totalHeight,
                    background: "rgba(0,0,0,0.06)",
                    zIndex: 2,
                  }}
                />
              );
            }
            const topPx = (overlay.startHour - HOURS_START) * HOUR_BLOCK_HEIGHT;
            const bottomPx = (overlay.endHour - HOURS_START) * HOUR_BLOCK_HEIGHT;
            const left = `calc(${hourLabelW}px + (100% - ${hourLabelW}px) * ${i / dayCount})`;
            const width = `calc((100% - ${hourLabelW}px) / ${dayCount})`;
            return (
              <React.Fragment key={`overlay-${date}`}>
                {topPx > 0 && (
                  <div className="absolute pointer-events-none" style={{ top: 0, left, width, height: topPx, background: "rgba(0,0,0,0.06)", zIndex: 2 }} />
                )}
                <div className="absolute pointer-events-none" style={{
                  top: topPx, left, width,
                  height: Math.max(bottomPx - topPx, 0),
                  background: "rgba(45, 212, 191, 0.08)",
                  borderTop: "2px solid rgba(45, 212, 191, 0.4)",
                  borderBottom: "2px solid rgba(45, 212, 191, 0.4)",
                  zIndex: 2,
                }} />
                {bottomPx < totalHeight && (
                  <div className="absolute pointer-events-none" style={{ top: bottomPx, left, width, height: totalHeight - bottomPx, background: "rgba(0,0,0,0.06)", zIndex: 2 }} />
                )}
              </React.Fragment>
            );
          });
        })()}
        <div className="flex flex-col">
          {hours.map((h) => {
            return (
              <div
                key={h}
                className="flex flex-row"
                style={{
                  height: HOUR_BLOCK_HEIGHT,
                  borderBottom: "2px solid #BDBDBD",
                }}
              >
                {/* Hour label */}
                <div
                  className="flex items-start text-h4 font-medium"
                  style={{
                    width: hourLabelWidth,
                    paddingLeft: 7,
                    paddingTop: 2,
                    fontFamily: "Poppins",
                    color: "#A1A1A1",
                    flexShrink: 0,
                  }}
                >
                  {h}h
                </div>
                {/* Minute column */}
                <div
                  className="flex flex-col"
                  style={{
                    width: minuteColWidth,
                    borderLeft: "1px solid #D9D9D9",
                    borderRight: "1px solid #D9D9D9",
                    flexShrink: 0,
                  }}
                >
                  {["00", "15", "30", "45"].map((m) => (
                    <div
                      key={m}
                      className="flex items-center justify-center text-h4 font-medium"
                      style={{
                        height: QUARTER_H,
                        borderBottom: "1px solid #D9D9D9",
                        fontFamily: "Poppins",
                        color: "#A1A1A1",
                      }}
                    >
                      {m}
                    </div>
                  ))}
                </div>

                {/* Day columns */}
                {weekDates.map((date) => {
                  const dispoZone = !isTousMode ? getDispoZone(blocks, worker!.id, date, h) : null;
                  const cellBg = dispoZone ? ZONE_BG[dispoZone] : undefined;
                  const dayJobs = (jobsByDay[date] || []).filter(
                    (j) => j.startHour >= h && j.startHour < h + 1,
                  );
                  const chargePreview =
                    gridPreview?.type === "charge" &&
                    (isTousMode || gridPreview.workerId === worker!.id) &&
                    gridPreview.date === date &&
                    gridPreview.startHour >= h &&
                    gridPreview.startHour < h + 1
                      ? gridPreview
                      : null;
                  const jobPreview =
                    gridPreview?.type === "job" &&
                    (isTousMode || gridPreview.workerId === worker!.id) &&
                    (!gridPreview.date || gridPreview.date === date) &&
                    gridPreview.startHour >= h &&
                    gridPreview.startHour < h + 1
                      ? gridPreview
                      : null;

                  return (
                    <div
                      key={date}
                      className="flex-1 relative"
                      style={{ borderRight: "1px solid #D9D9D9" }}
                      data-worker-col={isTousMode ? "tous" : worker!.id}
                      data-day-date={date}
                    >
                      {/* One cell per quarter-hour */}
                      {[0, 1, 2, 3].map((q) => (
                        <div
                          key={q}
                          style={{
                            height: QUARTER_H,
                            borderBottom: "1px solid #D9D9D9",
                            background: cellBg ?? undefined,
                          }}
                        />
                      ))}

                      {/* Non-working hours (no dispo block / congé) — greyed out for the single worker */}
                      {!isTousMode &&
                        offSegmentsInHour(h, offIntervalsByDate[date] || []).map(([os, oe], idx) => (
                          <div
                            key={`off-${idx}`}
                            className="absolute left-0 right-0 pointer-events-none"
                            style={{
                              top: ((os - h) / SNAP_HOUR) * SNAP_PX,
                              height: ((oe - os) / SNAP_HOUR) * SNAP_PX,
                              background: offSegmentBg(os, oe, offIntervalsByDate[date] || []),
                              zIndex: 3,
                            }}
                          />
                        ))}

                      {/* Jobs */}
                      {dayJobs.map((job) => {
                        const config = getJobDisplayConfig(job);
                        const isDragSource = gridPreview?.type === "job" && gridPreview.jobId === job.id;
                        // Travail ou charge du même ordre lâché dessus : les deux se fondent en un magnet.
                        const isMergeTarget = !!gridPreview?.mergeTargetJobId && gridPreview.mergeTargetJobId === job.id;
                        const outsideWindow = isJobOutsideCarWindow(job);
                        const isHovered = hoveredJobId === job.id;
                        const isHighlighted = (highlightJobId != null && job.id === highlightJobId) || (highlightOrdreId != null && job.ordre_id === highlightOrdreId);
                        const topPx = ((job.startHour - h) / SNAP_HOUR) * SNAP_PX;
                        const heightPx = (job.durationHours / SNAP_HOUR) * SNAP_PX;
                        const isAuto = job.autoGenerated;
                        const overlap = overlapByDay[date]?.get(job.id);
                        const olLeft = overlap ? `calc(${(overlap.column / overlap.totalColumns) * 100}% + 2px)` : "2px";
                        const olWidth = overlap ? `calc(${(1 / overlap.totalColumns) * 100}% - 4px)` : "calc(100% - 4px)";
                        return (
                          <div
                            key={job.id}
                            className={`absolute flex flex-col cursor-grab${isAuto ? " auto-placed-pulse" : ""}${isHighlighted ? " highlight-pulse" : ""}`}
                            style={{
                              left: olLeft,
                              width: olWidth,
                              top: topPx,
                              height: Math.max(heightPx, 20),
                              background: isMergeTarget ? "#E0E7FF" : outsideWindow ? "#FEE2E2" : config.bgColor,
                              border: isMergeTarget
                                ? "2px solid #6366F1"
                                : isHighlighted
                                ? "2px solid #6366F1"
                                : isAuto
                                ? `2px dashed ${outsideWindow ? "#FCA5A5" : config.borderColor}`
                                : `1px solid ${outsideWindow ? "#FCA5A5" : config.borderColor}`,
                              boxShadow: isMergeTarget
                                ? "0px 0px 16px rgba(99, 102, 241, 0.5)"
                                : isHighlighted
                                ? "0px 0px 12px rgba(99, 102, 241, 0.5)"
                                : isHovered
                                ? "0px 4px 12px rgba(0, 0, 0, 0.25)"
                                : undefined,
                              borderRadius: 5,
                              padding: "3px 4px",
                              overflow: isMergeTarget || (isHovered && heightPx < 24) ? "visible" : "hidden",
                              // Survolé : au-dessus de la ligne « maintenant » (25), sinon elle barre la bulle.
                              zIndex: isDragSource ? 5 : isMergeTarget ? 20 : isHovered ? 30 : 10,
                              margin: "0 2px",
                              opacity: myoPendingJobIds.includes(job.id) ? 0.55 : isDragSource ? 0.35 : (hoveredJobId && !isHovered && !isMergeTarget) ? 0.3 : isAuto ? 0.85 : 1,
                              ...(myoPendingJobIds.includes(job.id) ? { outline: "2px dashed #6366F1", outlineOffset: -2 } : {}),
                              touchAction: "none",
                              transition: "opacity 0.15s, box-shadow 0.15s, background 0.15s, border 0.15s",
                            }}
                            onPointerDown={(e) => handlePointerDown(e, job, "move")}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!job.ordre_id) return;
                              const ordre = getOrdreById(job.ordre_id);
                              // Le store des ordres ne contient que ce que l'écran « Ordres »
                              // a chargé : le statut servi avec le job suffit au menu.
                              const currentStatus = ordre?.status || (job.ordreStatus as OrdreStatus | undefined);
                              if (!currentStatus) return;
                              setContextMenu({
                                x: e.clientX,
                                y: e.clientY,
                                ordreId: job.ordre_id,
                                currentStatus,
                                itemActions: jobMenuActions(job),
                              });
                            }}
                            onPointerMove={handlePointerMoveLocal}
                            onPointerUp={(e) => handlePointerUp(e, job)}
                            onMouseEnter={() => setHoveredJobId(job.id)}
                            onMouseLeave={() => setHoveredJobId(null)}
                          >
                            {/* Hover tooltip */}
                            {isHovered && !gridPreview && (
                              <div
                                className="fixed z-50 bg-neutral-800 text-white rounded-lg px-3 py-2 pointer-events-none"
                                style={{ maxWidth: 320, minWidth: 140 }}
                                ref={(el) => { if (el) positionTooltip(el); }}
                              >
                                <div className="text-h4 font-medium">{job.clientName}<OrdreNumHint ordreId={job.ordre_id} /></div>
                                {job.vehicleName && <div className="text-h5 text-neutral-200">{job.vehicleName}</div>}
                                <OrdrePlateHint plate={job.plate} ordreId={job.ordre_id} />
                                <div className="text-h5 text-neutral-300">
                                  {formatTime(job.startHour)} — {formatTime(job.startHour + job.durationHours)}
                                </div>
                                {job.carWindow && (
                                  <div className="text-h5 text-neutral-400 mt-1">
                                    Réception : {formatDateHour(job.carWindow.depositDate, job.carWindow.depositStartHour)}
                                    {" — "}
                                    Restitution : {formatDateHour(job.carWindow.restitutionDate, job.carWindow.restitutionStartHour)}
                                  </div>
                                )}
                                {job.interventions && job.interventions.length > 0 && (
                                  <div className="text-h5 text-neutral-400 mt-1">
                                    {job.interventions.slice(0, 4).join(", ")}
                                    {job.interventions.length > 4 && ` +${job.interventions.length - 4}`}
                                  </div>
                                )}
                                {outsideWindow && (
                                  <div className="text-h5 text-red-400 mt-1 font-medium">Véhicule absent à cet horaire</div>
                                )}
                                {/* Même bloc que la bulle du planning réception : on lit
                                    le statut de l'OR sans quitter l'atelier. */}
                                <OrdreStatusHint status={jobOrdreStatus(job)} />
                                {isAuto
                                  ? <div className="text-h5 text-indigo-300 mt-1">Placement automatique - cliquer pour confirmer</div>
                                  : <div className="text-h5 text-neutral-400 mt-1">Cliquer pour modifier</div>
                                }
                              </div>
                            )}
                            <div className="flex flex-col" style={{ minWidth: 0, overflow: "hidden", flex: 1 }}>
                              <div className="flex flex-row items-center" style={{ gap: 3, minWidth: 0 }}>
                                {isAuto && (
                                  <span
                                    style={{
                                      fontSize: 9,
                                      fontWeight: 600,
                                      color: "#FFF",
                                      background: "#6366F1",
                                      borderRadius: 3,
                                      padding: "0 3px",
                                      lineHeight: "14px",
                                      flexShrink: 0,
                                    }}
                                  >
                                    Auto
                                  </span>
                                )}
                                {isTousMode && workerNameById[job.workerId] && (
                                  <span
                                    style={{
                                      fontSize: 8,
                                      fontWeight: 600,
                                      color: "#6B7280",
                                      background: "#F3F4F6",
                                      borderRadius: 3,
                                      padding: "0 3px",
                                      lineHeight: "13px",
                                      flexShrink: 0,
                                    }}
                                  >
                                    {workerNameById[job.workerId]}
                                  </span>
                                )}
                                <span
                                  className="text-h4 font-medium"
                                  style={{
                                    lineHeight: "140%",
                                    color: outsideWindow ? "#DC2626" : config.color,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    flex: 1,
                                  }}
                                >
                                  {job.clientName}
                                </span>
                              </div>
                              {heightPx > 30 && (
                                <span className="text-h5" style={{ color: config.color, opacity: 0.7 }}>
                                  {formatTime(job.startHour)} – {formatTime(job.startHour + job.durationHours)}
                                </span>
                              )}
                              {heightPx > 30 && job.vehicleName && (
                                <span className="text-h5" style={{ color: outsideWindow ? "#DC2626" : config.color, opacity: 0.8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {job.vehicleName}
                                </span>
                              )}
                              {heightPx > 30 && job.plate && (
                                <span className="text-h5" style={{ color: outsideWindow ? "#DC2626" : config.color, opacity: 0.7, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {job.plate}
                                </span>
                              )}
                              {heightPx > 60 && job.interventions && job.interventions.length > 0 && (
                                <span className="text-h5" style={{ color: outsideWindow ? "#DC2626" : config.color, opacity: 0.6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {job.interventions.slice(0, 2).join(", ")}
                                  {job.interventions.length > 2 && ` +${job.interventions.length - 2}`}
                                </span>
                              )}
                            </div>
                            {/* Resize handle — sous le bloc, au survol, quand le job est trop court
                                pour la porter sans masquer la zone de déplacement */}
                            {(heightPx >= 24 || (isHovered && !gridPreview)) && (
                            <div
                              className="absolute left-0 right-0 flex justify-center items-center"
                              style={heightPx >= 24
                                ? { bottom: 0, height: 8, cursor: "ns-resize" }
                                : { bottom: -10, height: 10, cursor: "ns-resize", zIndex: 1 }}
                              onPointerDown={(e) => {
                                e.stopPropagation();
                                handlePointerDown(e, job, "resize");
                              }}
                              onPointerMove={handlePointerMoveLocal}
                            >
                              <div
                                style={{
                                  width: 20,
                                  height: 3,
                                  borderRadius: 2,
                                  background: isMergeTarget ? "#6366F1" : config.borderColor,
                                  opacity: heightPx >= 24 ? 0.5 : 0.8,
                                }}
                              />
                            </div>
                            )}
                            {/* Merge preview: dashed extension showing added duration */}
                            {isMergeTarget && gridPreview && (() => {
                              const draggedDuration = (gridPreview as JobDragPreview).durationHours;
                              const mergedTotal = job.durationHours + draggedDuration;
                              const extensionPx = (draggedDuration / SNAP_HOUR) * SNAP_PX;
                              return (
                                <div
                                  className="absolute left-0 right-0 flex flex-col items-center justify-center pointer-events-none"
                                  style={{
                                    top: heightPx,
                                    height: extensionPx,
                                    background: "rgba(99, 102, 241, 0.08)",
                                    border: "2px dashed #6366F1",
                                    borderTop: "none",
                                    borderRadius: "0 0 5px 5px",
                                    margin: "0 2px",
                                    zIndex: 20,
                                  }}
                                >
                                  <span className="text-h4 font-medium" style={{ color: "#6366F1", lineHeight: "100%" }}>
                                    {formatTime(job.startHour)} - {formatTime(job.startHour + mergedTotal)} ({mergedTotal}h)
                                  </span>
                                </div>
                              );
                            })()}
                          </div>
                        );
                      })}

                      {/* Charge drop preview */}
                      {chargePreview && (() => {
                        const topPx = ((chargePreview.startHour - h) / SNAP_HOUR) * SNAP_PX;
                        const heightPx = (chargePreview.durationHours / SNAP_HOUR) * SNAP_PX;
                        // Check if preview is outside car window or overlaps another job
                        let previewOutside = !!chargePreview.overlap;
                        if (activeCarWindow?.depositDate) {
                          const depDate = activeCarWindow.depositDate;
                          const restDate = activeCarWindow.restitutionDate || depDate;
                          const previewEnd = chargePreview.startHour + chargePreview.durationHours;
                          if (date < depDate || date > restDate) previewOutside = true;
                          else if (date === depDate && activeCarWindow.depositStartHour != null && chargePreview.startHour < activeCarWindow.depositStartHour) previewOutside = true;
                          else if (date === restDate && activeCarWindow.restitutionStartHour != null && previewEnd > activeCarWindow.restitutionStartHour) previewOutside = true;
                        }
                        const previewColor = previewOutside ? "#DC2626" : "#6366F1";
                        const previewBg = previewOutside ? "rgba(220, 38, 38, 0.15)" : "rgba(99, 102, 241, 0.15)";
                        return (
                          <div
                            className="absolute left-0 right-0"
                            style={{
                              top: topPx,
                              height: Math.max(heightPx, 20),
                              background: previewBg,
                              border: `2px dashed ${previewColor}`,
                              borderRadius: 5,
                              padding: "3px 4px",
                              zIndex: 20,
                              margin: "0 2px",
                              pointerEvents: "none",
                            }}
                          >
                            <span
                              className="text-h4 font-medium"
                              style={{
                                color: previewColor,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {previewOutside && "⚠ "}{chargePreview.clientName}
                            </span>
                          </div>
                        );
                      })()}

                      {/* Job drag preview */}
                      {jobPreview && (() => {
                        const previewJob = allJobs.find((j) => j.id === jobPreview.jobId) || draggedJobRef.current;
                        if (!previewJob) return null;
                        const config = getJobDisplayConfig(previewJob);
                        const topPx = ((jobPreview.startHour - h) / SNAP_HOUR) * SNAP_PX;
                        const heightPx = (jobPreview.durationHours / SNAP_HOUR) * SNAP_PX;
                        // Check if preview is outside car window or overlaps another job
                        let previewOutside = !!jobPreview.overlap;
                        if (activeCarWindow?.depositDate) {
                          const depDate = activeCarWindow.depositDate;
                          const restDate = activeCarWindow.restitutionDate || depDate;
                          const previewEnd = jobPreview.startHour + jobPreview.durationHours;
                          if (date < depDate || date > restDate) previewOutside = true;
                          else if (date === depDate && activeCarWindow.depositStartHour != null && jobPreview.startHour < activeCarWindow.depositStartHour) previewOutside = true;
                          else if (date === restDate && activeCarWindow.restitutionStartHour != null && previewEnd > activeCarWindow.restitutionStartHour) previewOutside = true;
                        }
                        const pColor = previewOutside ? "#DC2626" : config.color;
                        const pBg = previewOutside ? "#FEE2E2" : config.bgColor;
                        const pBorder = previewOutside ? "#FCA5A5" : config.borderColor;
                        return (
                          <div
                            className="absolute left-0 right-0"
                            style={{
                              top: topPx,
                              height: Math.max(heightPx, 20),
                              background: pBg,
                              border: `2px dashed ${pBorder}`,
                              borderRadius: 5,
                              padding: "3px 4px",
                              zIndex: 20,
                              margin: "0 2px",
                              pointerEvents: "none",
                              opacity: 0.7,
                            }}
                          >
                            <span
                              className="text-h4 font-medium"
                              style={{
                                color: pColor,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {previewOutside && "⚠ "}{previewJob.clientName}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {selectedOrdre && (
        <OrdreModal
          ordre={selectedOrdre}
          onClose={() => setSelectedOrdre(null)}
          onSave={(updated) => setSelectedOrdre(updated)}
          onDelete={isOwnerOrAdmin(userData) ? () => setSelectedOrdre(null) : undefined}
        />
      )}
      {contextMenu && (
        <PlanningContextMenu menu={contextMenu} onClose={() => setContextMenu(null)} />
      )}
    </div>
  );
}
